import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ScanOutlined, CheckCircleOutlined, WarningOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { PageHeader, Breadcrumb, Row, Col, Card, Input, Button, Tabs, Badge, Alert, Descriptions, Spin, Tag, message } from 'antd';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { Scanner } from '@yudiel/react-qr-scanner';
import IntlMessages from '../../../../util/IntlMessages';
import { formatDateTime } from '../../../../util/date';
import BreadcrumbHomeLink from '../../../../components/BreadcrumbHomeLink';
import { defaultCatchException } from '../../../../apollo/callbacks';
import { PLAYER_BY_ENTRY_TOKEN } from '../../../../apollo/query/entry';
import { CONFIRM_PLAYER_ENTRY } from '../../../../apollo/mutation/entry';
import { EVENT_CONTAINER_DATA } from '../../../../apollo/query/event';
import { SUCCESS_COLOR } from '../../../../constants/ThemeSetting';
import { getAdminPath } from '../../../../util/router';

const TOKEN_URL_REGEX = /\/entry\/([a-f0-9-]+)/i;
const TOKEN_UUID_REGEX = /^[a-f0-9-]{36}$/i;

const getEntryErrorMessage = (error, intl) => {
    const gqlError = error?.graphQLErrors?.[0];
    const messageKey = gqlError?.extensions?.response?.message || gqlError?.message || 'server_error.player_not_found';
    return intl.formatMessage({ id: messageKey });
};

const extractToken = (input) => {
    if (!input) return null;
    const urlMatch = input.match(TOKEN_URL_REGEX);
    if (urlMatch) return urlMatch[1];
    const trimmed = input.trim();
    if (TOKEN_UUID_REGEX.test(trimmed)) return trimmed;
    return null;
};

const PlayerCard = ({ player, onConfirm, confirmLoading, event, intl }) => {
    if (!player) return null;

    const alreadyEntered = !!player.entry_at;

    return (
        <Card className="gx-card gx-mt-3">
            <Descriptions column={1} bordered size="small">
                <Descriptions.Item label={<IntlMessages id="common.name" />}>
                    {player.emp_player_url ? (
                        <Link to={player.emp_player_url}>
                            {player.first_name} {player.last_name}
                        </Link>
                    ) : (
                        <>{player.first_name} {player.last_name}</>
                    )}
                </Descriptions.Item>
                <Descriptions.Item label={<IntlMessages id="common.email" />}>
                    {player.email}
                </Descriptions.Item>
                <Descriptions.Item label={<IntlMessages id="common.event" />}>
                    {player.event_name}
                </Descriptions.Item>
                <Descriptions.Item label={<IntlMessages id={`common.event_type_${event.type}`} />}>
                    {player.sub_event_name}
                </Descriptions.Item>
                {player.custom_fields?.filter((f) => f.value).map((field) => (
                    <Descriptions.Item key={field.name} label={field.label}>
                        {intl.messages[`common.${field.value}`]
                            ? intl.formatMessage({ id: `common.${field.value}` })
                            : field.value}
                    </Descriptions.Item>
                ))}
                {player.product_variants?.map((variant) => (
                    <Descriptions.Item key={variant.sku} label={variant.product?.name}>
                        {(variant.attributes || []).map((attr, i) => (
                            <Tag key={i}>{attr.attribute_name_locale}: {attr.option_name_locale}</Tag>
                        ))}
                    </Descriptions.Item>
                ))}
            </Descriptions>

            {!player.confirmed && (
                <Alert
                    className="gx-mt-3"
                    type="warning"
                    showIcon
                    icon={<WarningOutlined className='gx-pr-1' />}
                    message={<IntlMessages id="access_management.not_confirmed" />}
                />
            )}

            {alreadyEntered ? (
                <Alert
                    className="gx-mt-3"
                    type="warning"
                    showIcon
                    icon={<WarningOutlined className='gx-pr-1' />}
                    message={intl.formatMessage(
                        { id: 'access_management.already_entered' },
                        { date: formatDateTime(player.entry_at) }
                    )}
                />
            ) : (
                <Button
                    className="gx-mt-3"
                    type="primary"
                    size="large"
                    block
                    loading={confirmLoading}
                    icon={<CheckCircleOutlined className='gx-pr-1' />}
                    onClick={onConfirm}
                >
                    <IntlMessages id="access_management.confirm_entry" />
                </Button>
            )}
        </Card>
    );
};

const PCMode = ({ pin, intl, setEntriesCount, event }) => {
    const inputRef = useRef(null);
    const [inputValue, setInputValue] = useState('');
    const [currentToken, setCurrentToken] = useState(null);
    const [player, setPlayer] = useState(null);
    const [scanError, setScanError] = useState(null);

    const [fetchPlayer, { loading: fetchLoading }] = useLazyQuery(PLAYER_BY_ENTRY_TOKEN, {
        fetchPolicy: 'no-cache',
    });

    const [confirmEntry, { loading: confirmLoading }] = useMutation(CONFIRM_PLAYER_ENTRY);

    const resetAndFocus = useCallback(() => {
        setPlayer(null);
        setScanError(null);
        setInputValue('');
        setCurrentToken(null);
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 100);
    }, []);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    const handleScan = useCallback((value) => {
        const token = extractToken(value);
        if (!token) {
            setScanError(intl.formatMessage({ id: 'access_management.player_not_found' }));
            setPlayer(null);
            setCurrentToken(null);
            return;
        }

        setScanError(null);
        setPlayer(null);
        setCurrentToken(token);

        fetchPlayer({ variables: { token, pin } })
            .then(({ data, error }) => {
                if (error || !data?.playerByEntryToken) {
                    setScanError(getEntryErrorMessage(error, intl));
                } else {
                    setPlayer(data.playerByEntryToken);
                }
            })
            .catch((e) => {
                setScanError(getEntryErrorMessage(e, intl));
            });
    }, [pin, fetchPlayer, intl]);

    const handleConfirm = useCallback(() => {
        if (!player || !currentToken) return;

        confirmEntry({ variables: { token: currentToken, pin } })
            .then(({ data, errors }) => {
                if (errors) {
                    message.error(intl.formatMessage({ id: 'common.default_mutation_error' }));
                } else if (data?.confirmPlayerEntry) {
                    message.success(intl.formatMessage({ id: 'access_management.entry_success' }));
                    setEntriesCount((prev) => prev + 1);
                    resetAndFocus();
                }
            })
            .catch((e) => defaultCatchException(e, intl));
    }, [player, currentToken, pin, confirmEntry, intl, resetAndFocus, setEntriesCount]);

    return (
        <div>
            <Row justify="center">
                <Col xs={24} md={16} lg={12}>
                    <Input.Search
                        ref={inputRef}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        placeholder={intl.formatMessage({ id: 'access_management.scanner.input_placeholder' })}
                        enterButton={<ScanOutlined />}
                        size="large"
                        loading={fetchLoading}
                        onSearch={handleScan}
                        autoFocus
                    />
                </Col>
            </Row>

            {fetchLoading && (
                <Row justify="center" className="gx-mt-3">
                    <Spin />
                </Row>
            )}

            {scanError && (
                <Row justify="center" className="gx-mt-3">
                    <Col xs={24} md={16} lg={12}>
                        <Alert
                            type="error"
                            showIcon
                            icon={<CloseCircleOutlined />}
                            message={scanError}
                            closable
                            onClose={() => setScanError(null)}
                        />
                    </Col>
                </Row>
            )}

            {player && (
                <Row justify="center">
                    <Col xs={24} md={16} lg={12}>
                        <PlayerCard
                            player={player}
                            onConfirm={handleConfirm}
                            confirmLoading={confirmLoading}
                            event={event}
                            intl={intl}
                        />
                    </Col>
                </Row>
            )}

            {!player && !scanError && !fetchLoading && (
                <Row justify="center" className="gx-mt-4">
                    <Col style={{ textAlign: 'center' }}>
                        <ScanOutlined style={{ fontSize: 48, color: '#bbb' }} />
                        <p className="gx-text-muted gx-mt-2">
                            <IntlMessages id="access_management.scanner.waiting" />
                        </p>
                    </Col>
                </Row>
            )}
        </div>
    );
};

const PhoneMode = ({ pin, intl, setEntriesCount, event }) => {
    const [player, setPlayer] = useState(null);
    const [scanError, setScanError] = useState(null);
    const [lastScannedToken, setLastScannedToken] = useState(null);
    const [scanKey, setScanKey] = useState(0);

    const [fetchPlayer, { loading: fetchLoading }] = useLazyQuery(PLAYER_BY_ENTRY_TOKEN, {
        fetchPolicy: 'no-cache',
    });

    const [confirmEntry, { loading: confirmLoading }] = useMutation(CONFIRM_PLAYER_ENTRY);

    const handleScanResult = useCallback((result) => {
        if (!result || !result[0]) return;

        const scannedValue = result[0].rawValue;
        const token = extractToken(scannedValue);

        if (!token) return;
        if (token === lastScannedToken) return;

        setLastScannedToken(token);
        setScanError(null);
        setPlayer(null);

        fetchPlayer({ variables: { token, pin } })
            .then(({ data, error }) => {
                if (error || !data?.playerByEntryToken) {
                    setScanError(getEntryErrorMessage(error, intl));
                } else {
                    setPlayer(data.playerByEntryToken);
                }
            })
            .catch((e) => {
                setScanError(getEntryErrorMessage(e, intl));
            });
    }, [pin, fetchPlayer, intl, lastScannedToken]);

    const handleReset = useCallback(() => {
        setPlayer(null);
        setLastScannedToken(null);
        setScanError(null);
        setScanKey((prev) => prev + 1);
    }, []);

    const handleConfirm = useCallback(() => {
        if (!player || !lastScannedToken) return;

        confirmEntry({ variables: { token: lastScannedToken, pin } })
            .then(({ data, errors }) => {
                if (errors) {
                    message.error(intl.formatMessage({ id: 'common.default_mutation_error' }));
                } else if (data?.confirmPlayerEntry) {
                    message.success(intl.formatMessage({ id: 'access_management.entry_success' }));
                    setEntriesCount((prev) => prev + 1);
                    handleReset();
                }
            })
            .catch((e) => defaultCatchException(e, intl));
    }, [player, lastScannedToken, pin, confirmEntry, intl, setEntriesCount, handleReset]);

    return (
        <div>
            <Row justify="center">
                <Col xs={24} md={16} lg={12}>
                    <Card className="gx-card" bodyStyle={{ padding: 0 }}>
                        <Scanner
                            key={scanKey}
                            onScan={handleScanResult}
                            styles={{ container: { width: '100%' } }}
                        />
                    </Card>
                </Col>
            </Row>

            {fetchLoading && (
                <Row justify="center" className="gx-mt-3">
                    <Spin />
                </Row>
            )}

            {scanError && (
                <Row justify="center" className="gx-mt-3">
                    <Col xs={24} md={16} lg={12}>
                        <Alert
                            type="error"
                            showIcon
                            icon={<CloseCircleOutlined />}
                            message={scanError}
                            closable
                            onClose={() => {
                                setScanError(null);
                                setLastScannedToken(null);
                            }}
                        />
                    </Col>
                </Row>
            )}

            {player && (
                <Row justify="center">
                    <Col xs={24} md={16} lg={12}>
                        <PlayerCard
                            event={event}
                            player={player}
                            onConfirm={handleConfirm}
                            confirmLoading={confirmLoading}
                            intl={intl}
                        />
                        <Button
                            className="gx-mt-2"
                            block
                            icon={<ScanOutlined className="gx-pr-1" />}
                            onClick={handleReset}
                        >
                            <IntlMessages id="access_management.scanner.scan_next" />
                        </Button>
                    </Col>
                </Row>
            )}
        </div>
    );
};

const EventScanner = ({ match }) => {
    const { params } = match;
    const intl = useIntl();
    const [entriesCount, setEntriesCount] = useState(0);

    const { data, loading, error } = useQuery(EVENT_CONTAINER_DATA, {
        variables: { id: params.id },
    });

    const event = data?.event;
    const pin = event?.access_management?.pin;
    const hasAccessManagement = event?.access_management?.enabled && event?.sub_events?.some((se) => se.access_management);

    if (loading) {
        return (
            <Row justify="center" className="gx-mt-5">
                <Spin size="large" />
            </Row>
        );
    }

    if (error || !event || !pin || !hasAccessManagement) {
        return (
            <>
                <Row>
                    <Col md={24}>
                        <Breadcrumb className="gx-pb-3">
                            <Breadcrumb.Item>
                                <BreadcrumbHomeLink />
                            </Breadcrumb.Item>
                            <Breadcrumb.Item>
                                <Link to={getAdminPath(`event/${params.id}/dashboard`)}>{event?.name}</Link>
                            </Breadcrumb.Item>
                            <Breadcrumb.Item>
                                <IntlMessages id="access_management.scanner" />
                            </Breadcrumb.Item>
                        </Breadcrumb>
                    </Col>
                </Row>
                <Row justify="center" className="gx-mt-5">
                    <Col xs={24} md={16} lg={12}>
                        <Alert
                            type="warning"
                            showIcon
                            message={intl.formatMessage({ id: 'access_management.not_configured' })}
                        />
                    </Col>
                </Row>
            </>
        );
    }

    return (
        <>
            <Row>
                <Col md={24}>
                    <Breadcrumb className="gx-pb-3">
                        <Breadcrumb.Item>
                            <BreadcrumbHomeLink />
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>
                            <Link to={getAdminPath(`event/${event.id}/dashboard`)}>{event.name}</Link>
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>
                            <IntlMessages id="access_management.scanner" />
                        </Breadcrumb.Item>
                    </Breadcrumb>
                </Col>
            </Row>

            <Row>
                <Col xs={24}>
                    <PageHeader
                        title={
                            <span>
                                <ScanOutlined className="gx-mr-2" />
                                <IntlMessages id="access_management.scanner" />
                            </span>
                        }
                        extra={[
                            <Badge
                                key="entries-count"
                                count={entriesCount}
                                showZero
                                style={{ backgroundColor: entriesCount > 0 ? SUCCESS_COLOR : '#999' }}
                                overflowCount={9999}
                            />,
                        ]}
                    />
                </Col>
            </Row>

            <Tabs defaultActiveKey="pc">
                <Tabs.TabPane
                    tab={<IntlMessages id="access_management.scanner.pc_mode" />}
                    key="pc"
                >
                    <PCMode
                        pin={pin}
                        intl={intl}
                        event={event}
                        setEntriesCount={setEntriesCount}
                    />
                </Tabs.TabPane>
                <Tabs.TabPane
                    tab={<IntlMessages id="access_management.scanner.phone_mode" />}
                    key="phone"
                >
                    <PhoneMode
                        pin={pin}
                        intl={intl}
                        event={event}
                        setEntriesCount={setEntriesCount}
                    />
                </Tabs.TabPane>
            </Tabs>
        </>
    );
};

export default EventScanner;
